import React, { useState, useEffect, useRef } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  IconButton, 
  Menu, 
  MenuItem, 
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  useMediaQuery,
  useTheme,
  Badge
} from '@mui/material';
import MuiLink from '@mui/material/Link';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import SportsMotorsportsIcon from '@mui/icons-material/SportsMotorsports';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import AddIcon from '@mui/icons-material/Add';
import DashboardIcon from '@mui/icons-material/Dashboard';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CloseIcon from '@mui/icons-material/Close';
import { getUserNotifications, getUnreadNotificationCount, markNotificationAsRead, markAllNotificationsAsRead, deleteOldNotifications } from '../services/notificationService';

function notificationDiscordHref(url) {
  const s = String(url || '').trim();
  if (!s) return '#';
  if (/^https?:\/\//i.test(s)) return s;
  return `https://${s.replace(/^\/+/, '')}`;
}

function Navbar() {
  const { isAuthenticated, logout, user, getEaId } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const notificationButtonRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    setAnchorEl(null);
    setMobileMenuOpen(false);
    setNotificationAnchorEl(null);
  }, [isAuthenticated, user]);

  // Set up notification listeners
  useEffect(() => {
    if (!isAuthenticated() || !user) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    // Get notifications
    const unsubscribeNotifications = getUserNotifications(user.uid, (notifs) => {
      console.log('Notifications received:', notifs);
      console.log('Number of notifications:', notifs.length);
      setNotifications(notifs);
    });

    // Get unread count
    const unsubscribeCount = getUnreadNotificationCount(user.uid, (count) => {
      console.log('Unread count:', count);
      setUnreadCount(count);
    });

    return () => {
      unsubscribeNotifications();
      unsubscribeCount();
    };
  }, [isAuthenticated, user]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileMenuOpen(false);
    setAnchorEl(null);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationMenuOpen = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.read) {
      try {
        await markNotificationAsRead(notification.id);
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }

    // Navigate based on notification type
    handleNotificationMenuClose();
    
    if (notification.type === 'application_received') {
      // Navigate to "Your Cards" section
      navigate('/create-card', { state: { scrollToActiveCards: true } });
    } else if (notification.type === 'application_accepted' || notification.type === 'application_rejected') {
      // Navigate to "Your Applications" section
      navigate('/race-cards');
      // Scroll to applications section after a short delay
      setTimeout(() => {
        const applicationsSection = document.getElementById('your-applications-section');
        if (applicationsSection) {
          applicationsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user) return;
    try {
      await markAllNotificationsAsRead(user.uid);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const handleClearOldNotifications = async () => {
    if (!user) return;
    try {
      const result = await deleteOldNotifications(user.uid, 50);
      console.log(`Deleted ${result.deleted} old notifications`);
    } catch (error) {
      console.error('Error clearing old notifications:', error);
    }
  };

  const handleMyAccount = () => {
    navigate('/my-account');
    handleMenuClose();
    setMobileMenuOpen(false);
  };

  const handleYourCards = () => {
    navigate('/create-card', { state: { scrollToActiveCards: true } });
    handleMenuClose();
    setMobileMenuOpen(false);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const displayName = getEaId() || user?.email || 'Account';

  const mobileMenuItems = [
    { text: 'Home', icon: <HomeIcon />, path: '/' },
    { text: 'Race Cards', icon: <SportsMotorsportsIcon />, path: '/race-cards' },
    { text: 'Create Race Card', icon: <AddIcon />, path: '/create-card' },
  ];

  // Add Your Cards to mobile menu if authenticated (before Help)
  if (isAuthenticated()) {
    mobileMenuItems.push({ text: 'Your Cards', icon: <DashboardIcon />, path: '/create-card', action: handleYourCards });
  }

  // Add Help after Your Cards
  mobileMenuItems.push({ text: 'Help', icon: <AccountCircleIcon />, path: '/help' });

  // Custom typography styles for navbar elements
  const navButtonStyle = {
    fontFamily: '"Orbitron", "Roboto", "Helvetica", "Arial", sans-serif',
    fontWeight: 600,
    fontSize: '1rem',
    textTransform: 'none',
    letterSpacing: '0.5px',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-1px)',
      textShadow: '0 2px 4px rgba(0,0,0,0.3)',
    }
  };

  const userDisplayStyle = {
    fontFamily: '"Orbitron", "Roboto", "Helvetica", "Arial", sans-serif',
    fontWeight: 700,
    fontSize: '0.95rem',
    letterSpacing: '0.3px',
    textShadow: '0 1px 2px rgba(0,0,0,0.2)',
  };

  const mobileMenuItemStyle = {
    fontFamily: '"Orbitron", "Roboto", "Helvetica", "Arial", sans-serif',
    fontWeight: 600,
    fontSize: '1.1rem',
    letterSpacing: '0.5px',
  };

  const MobileMenu = () => (
    <Drawer
      anchor="left"
      open={mobileMenuOpen}
      onClose={() => setMobileMenuOpen(false)}
      sx={{
        '& .MuiDrawer-paper': {
          backgroundColor: theme.palette.primary.main,
          color: 'white',
        }
      }}
    >
      <Box sx={{ width: 250, pt: 2 }}>
        <RouterLink to="/" style={{ display: 'block', textAlign: 'center', marginBottom: 16 }}>
          <img
            src="/images/race-with-me-logo.png"
            alt="RaceWithMe - F1 Game Racing Community Logo"
            style={{ height: '50px', cursor: 'pointer' }}
          />
        </RouterLink>
        <List>
          {mobileMenuItems.map((item) => (
            <ListItem 
              key={item.text} 
              button 
              onClick={() => {
                if (item.action) {
                  item.action();
                } else {
                  handleNavigation(item.path);
                }
              }}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
            >
              <ListItemIcon sx={{ color: 'white' }}>{item.icon}</ListItemIcon>
              <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{ sx: mobileMenuItemStyle }}
              />
            </ListItem>
          ))}
        </List>
        <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)' }} />
        {isAuthenticated() ? (
          <List>
            <ListItem sx={{ color: 'white' }}>
              <ListItemIcon sx={{ color: 'white' }}><PersonIcon /></ListItemIcon>
              <ListItemText 
                primary={displayName} 
                primaryTypographyProps={{ sx: userDisplayStyle }}
              />
            </ListItem>
            <ListItem 
              button 
              onClick={handleMyAccount}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
            >
              <ListItemIcon sx={{ color: 'white' }}><AccountCircleIcon /></ListItemIcon>
              <ListItemText 
                primary="My Account" 
                primaryTypographyProps={{ sx: mobileMenuItemStyle }}
              />
            </ListItem>
            <ListItem 
              button 
              onClick={handleLogout}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
            >
              <ListItemIcon sx={{ color: 'white' }}><LogoutIcon /></ListItemIcon>
              <ListItemText 
                primary="Logout" 
                primaryTypographyProps={{ sx: mobileMenuItemStyle }}
              />
            </ListItem>
          </List>
        ) : (
          <List>
            <ListItem 
              button 
              onClick={() => handleNavigation('/login')}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
            >
              <ListItemText 
                primary="Login" 
                primaryTypographyProps={{ sx: mobileMenuItemStyle }}
              />
            </ListItem>
            <ListItem 
              button 
              onClick={() => handleNavigation('/register')}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
            >
              <ListItemText 
                primary="Register" 
                primaryTypographyProps={{ sx: mobileMenuItemStyle }}
              />
            </ListItem>
          </List>
        )}
      </Box>
    </Drawer>
  );

  return (
    <AppBar position="static">
      <Toolbar>
        <Box sx={{ flexGrow: 1 }}>
          <RouterLink to="/" style={{ display: 'inline-block', textDecoration: 'none' }}>
            <img
              src="/images/race-with-me-logo.png"
              alt="RaceWithMe - F1 Game Racing Community Logo"
              style={{ height: '50px', cursor: 'pointer', verticalAlign: 'middle' }}
            />
          </RouterLink>
        </Box>
        
        {isMobile ? (
          <>
            {isAuthenticated() && (
              <IconButton 
                color="inherit" 
                onClick={handleNotificationMenuOpen}
                sx={{ mr: 1 }}
              >
                <Badge badgeContent={unreadCount} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            )}
            <IconButton 
              color="inherit" 
              onClick={() => setMobileMenuOpen(true)}
              edge="end"
            >
              <MenuIcon />
            </IconButton>
            <MobileMenu />
          </>
        ) : (
          <Box>
            <Button 
              color="inherit" 
              component={RouterLink} 
              to="/"
              sx={navButtonStyle}
            >
              Home
            </Button>
            <Button 
              color="inherit" 
              component={RouterLink} 
              to="/race-cards"
              sx={navButtonStyle}
            >
              Race Cards
            </Button>
            <Button 
              color="inherit" 
              component={RouterLink} 
              to="/create-card"
              sx={navButtonStyle}
            >
              Create Card
            </Button>
            {isAuthenticated() && (
              <Button 
                color="inherit" 
                onClick={handleYourCards}
                sx={navButtonStyle}
              >
                Your Cards
              </Button>
            )}
            <Button 
              color="inherit" 
              component={RouterLink} 
              to="/help"
              sx={navButtonStyle}
            >
              Help
            </Button>
            {isAuthenticated() ? (
              <>
                <IconButton 
                  color="inherit" 
                  onClick={handleNotificationMenuOpen}
                  sx={{ ml: 1 }}
                  ref={notificationButtonRef}
                >
                  <Badge badgeContent={unreadCount} color="error">
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
                <IconButton color="inherit" onClick={handleMenuOpen} sx={{ ml: 1 }}>
                  <AccountCircleIcon />
                  <Typography variant="body1" sx={{ ml: 1, ...userDisplayStyle }}>
                    {displayName}
                  </Typography>
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  sx={{ mt: 1 }}
                >
                  <MenuItem 
                    onClick={handleMyAccount}
                    sx={{ 
                      fontFamily: '"Orbitron", "Roboto", "Helvetica", "Arial", sans-serif',
                      fontWeight: 600,
                      letterSpacing: '0.3px'
                    }}
                  >
                    My Account
                  </MenuItem>
                  <MenuItem 
                    onClick={handleLogout}
                    sx={{ 
                      fontFamily: '"Orbitron", "Roboto", "Helvetica", "Arial", sans-serif',
                      fontWeight: 600,
                      letterSpacing: '0.3px'
                    }}
                  >
                    Logout
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <>
                <Button 
                  color="inherit" 
                  component={RouterLink} 
                  to="/login"
                  sx={navButtonStyle}
                >
                  Login
                </Button>
                <Button 
                  color="inherit" 
                  component={RouterLink} 
                  to="/register"
                  sx={navButtonStyle}
                >
                  Register
                </Button>
              </>
            )}
          </Box>
        )}
        
        {/* Notification Menu - Shared between mobile and desktop */}
        {isAuthenticated() && (
          <Menu
            anchorEl={notificationAnchorEl}
            open={Boolean(notificationAnchorEl)}
            onClose={handleNotificationMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            disableScrollLock={false}
            sx={{ 
              mt: 1,
              zIndex: (theme) => theme.zIndex.drawer + 1
            }}
            MenuListProps={{
              sx: {
                py: 0
              }
            }}
            PaperProps={{
              sx: {
                maxHeight: 400,
                width: isMobile ? '90vw' : '350px',
                maxWidth: isMobile ? '90vw' : '350px',
                mt: 1
              }
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              px: 2,
              py: 1.5,
              borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
            }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontFamily: '"Orbitron", "Roboto", "Helvetica", "Arial", sans-serif',
                  fontWeight: 700,
                  fontSize: '1rem',
                  letterSpacing: '0.5px'
                }}
              >
                Notifications
              </Typography>
              <IconButton 
                size="small" 
                onClick={handleNotificationMenuClose}
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
              {notifications.length === 0 ? (
                <MenuItem 
                  disabled
                  sx={{
                    py: 3
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No notifications
                  </Typography>
                </MenuItem>
              ) : (
              <>
                {notifications.map((notification) => (
                <MenuItem
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    fontFamily: '"Orbitron", "Roboto", "Helvetica", "Arial", sans-serif',
                    fontWeight: notification.read ? 400 : 600,
                    letterSpacing: '0.3px',
                    backgroundColor: notification.read ? 'transparent' : 'rgba(25, 118, 210, 0.08)',
                    borderLeft: notification.read ? 'none' : '3px solid #1976d2',
                    py: 1.5,
                    pl: 0,
                    pr: 2,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word'
                  }}
                >
                  <Box sx={{ pl: 2 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: notification.read ? 400 : 600,
                        mb: 0.5
                      }}
                    >
                      {notification.message}
                      {notification.type === 'application_accepted' && notification.discordLink ? (
                        <>
                          {' '}or you can join their league Discord{' '}
                          <MuiLink
                            href={notificationDiscordHref(notification.discordLink)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            sx={{ fontWeight: 'inherit', color: 'inherit' }}
                          >
                            here
                          </MuiLink>
                          .
                        </>
                      ) : null}
                    </Typography>
                    {notification.createdAt && (
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: '0.7rem' }}
                      >
                        {notification.createdAt?.toDate 
                          ? new Date(notification.createdAt.toDate()).toLocaleString()
                          : notification.createdAt?.seconds
                          ? new Date(notification.createdAt.seconds * 1000).toLocaleString()
                          : new Date(notification.createdAt).toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
                ))}
                <Divider />
                <Box sx={{ p: 1 }}>
                  <Button
                    fullWidth
                    size="small"
                    onClick={handleMarkAllAsRead}
                    disabled={unreadCount === 0}
                    sx={{
                      fontFamily: '"Orbitron", "Roboto", "Helvetica", "Arial", sans-serif',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      mb: 0.5
                    }}
                  >
                    Mark All as Read
                  </Button>
                  <Button
                    fullWidth
                    size="small"
                    onClick={handleClearOldNotifications}
                    disabled={notifications.length === 0}
                    sx={{
                      fontFamily: '"Orbitron", "Roboto", "Helvetica", "Arial", sans-serif',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      color: 'text.secondary'
                    }}
                  >
                    Clear notifications
                  </Button>
                </Box>
              </>
            )}
          </Menu>
        )}
      </Toolbar>
    </AppBar>
  );
}

export default Navbar; 